/**
 * PendingCRBadge — Task 4 (2026-06-09).
 *
 * Renders a blue "Pending Approval" badge on any row that has one or
 * more pending Change Requests filed against it. Click-through navigates
 * to /change-requests/<id> (single CR) or /change-requests (filtered, when
 * multiple). Tooltip on hover lists each CR with kind + filed-by + filed-at.
 *
 * Visual language (Pedro 2026-06-09):
 *   - "Pending Save"     — orange — pre-Save Pattern A staging
 *   - "Pending Approval" — blue   — post-Save CR awaiting Compliance
 *
 * Returns null when crs is empty so callers can render unconditionally:
 *
 *     <PendingCRBadge crs={row.pending_change_requests} />
 */
import { Badge, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';

import type { PendingChangeRequestRef } from '../api/types';
import { useCurrentUser } from '../hooks/useAuth';

interface Props {
  crs: PendingChangeRequestRef[] | undefined;
  /** Optional lookup for resolving requested_by UUIDs → display names in the
   *  tooltip. When absent the tooltip shows a UUID prefix. */
  usersById?: Map<string, string>;
}

const KIND_LABEL: Record<string, string> = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  RESTORE: 'Restore',
};

export function PendingCRBadge({ crs, usersById }: Props) {
  const { user } = useCurrentUser();
  if (!crs || crs.length === 0) return null;

  // Tooltip: one line per CR. Pre-line whitespace preserved via Mantine styles.
  // Maker name comes from backend (requested_by_name) when present; falls
  // back to the optional usersById map or a UUID prefix.
  const lines = crs.map((cr) => {
    // #26 polish (2026-06-10, Pedro): prefer the server-derived nature
    // of the operation ("Detach", "Terminate · LAPSED", "Attach to
    // Agreement", …) over the bare CR kind.
    const kind = cr.op_label ?? KIND_LABEL[cr.kind] ?? cr.kind;
    const filer = cr.requested_by_name
      ?? usersById?.get(cr.requested_by)
      ?? `(${cr.requested_by.slice(0, 8)})`;
    let when: string;
    try {
      when = new Date(cr.requested_at).toLocaleString();
    } catch {
      when = cr.requested_at;
    }
    return `${kind} filed by ${filer} · ${when}`;
  });
  const tooltipLabel = lines.join('\n');

  // Single CR → deep-link to its detail page. Multiple → the review queue for Admin /
  // Compliance; everyone else to My Change Requests (the queue is Admin / Compliance only
  // since 2026-10-11, C-356 / C-358). Filter-by-entity is a future polish.
  const checker = user?.role === 'admin' || user?.role === 'compliance';
  const to = crs.length === 1
    ? `/change-requests/${crs[0].id}`
    : checker ? '/change-requests' : '/my-change-requests';

  // #26 polish: the badge itself names the operation — "Pending · Detach"
  // — so the user knows WHAT is pending without hovering. "Approval" is
  // dropped when an op label is present to keep the badge short enough
  // for narrow table columns (blue already encodes "filed, awaiting
  // Compliance"; the tooltip spells it out). Compound labels like
  // "Terminate · LAPSED" keep only their first segment on the badge —
  // the reason stays in the tooltip.
  const op = crs.length === 1 ? crs[0].op_label : null;
  const opShort = op ? op.split('·')[0].trim() : null;
  const label = crs.length === 1
    ? (opShort ? `Pending · ${opShort}` : 'Pending Approval')
    : `Pending Approval (${crs.length})`;

  return (
    <Tooltip
      label={tooltipLabel}
      multiline
      withArrow
      position="top"
      styles={{ tooltip: { whiteSpace: 'pre-line' } }}
    >
      <Badge
        component={Link}
        to={to}
        color="blue"
        variant="light"
        size="sm"
        style={{ cursor: 'pointer' }}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}
